/**
 * @author       Andreas Bayer <andreas@plusbyte.de>
 * @copyright    2015 Plusbyte UG (haftungsbeschränkt)
 * @license      {@link https://plusbyte.de Plusbyte License}
 */

import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import utc from 'dayjs/plugin/utc';
import { v4 as uuid } from 'uuid';

import {
  BOT_PACING_LOCAL_TRAINING,
  pacingProfileFromBaseDelayMs,
  sampleBotThinkDelayMs,
} from './botPacing';
import {
  ChatVisibility,
  EventLogEntry,
  GameOverview,
  GameStatus,
  GameType,
  GameVisibility,
} from './dtos';
import {
  Computer as ComputerProp,
  Horse as HorseProp,
  JoinGameRequest,
  NewGameRequest,
  Player as PlayerProp,
} from './dtos/controller';
import { Controller, ControllerType, View } from './interfaces';
import { gameLogger } from './logger';
import RandomNames from './randomnames';
import Computer from './tmcomputer';
import Turfmaster, {
  Board,
  Field,
  Game,
  Horse,
  Move,
  Player,
  StateMachineValue,
  StateMatrixKey,
} from './tmmodel';
import { stateToString, USERID_FOR_SERVER_MESSAGES, wait } from './utilConstants';
import { randomInt } from './utilRandom';
/**
 * Controller
 * @class Turfmaster.Controller
 * @constructor
 */
class ControllerLocal implements Controller {
  private randomNames: RandomNames;

  public gameid = '';

  public timecreated = '';

  public playerViews: View[] = [];

  public board: Board = new Board(0);

  public game: Game = new Game();

  public eventLog: EventLogEntry[] = [];

  public lastRemovedCards: number[] = [];

  public gameType: GameType = GameType.Training;

  public visibility: GameVisibility = 'public';

  public allowSpectators = true;

  public chatVisibility: ChatVisibility = 'public';

  /** Pacing for dice result pause and other server-side delays (matches game environment). */
  public pacingProfile = BOT_PACING_LOCAL_TRAINING;

  public constructor(
    gameType: GameType = GameType.Training,
    gameid = '',
    timecreated = '',
    lastRemovedCards: number[] = [],
    game: Game = new Game(),
    eventLog: EventLogEntry[] = [],
    boardId = 0,
    visibility: GameVisibility = 'public',
    allowSpectators = true,
    chatVisibility: ChatVisibility = 'public',
  ) {
    // configure dayjs
    dayjs.extend(isoWeek);
    dayjs.extend(utc);
    if (timecreated === '') {
      timecreated = dayjs().utc().toISOString();
    }

    this.randomNames = new RandomNames();

    this.gameType = gameType;
    this.gameid = gameid;
    this.timecreated = timecreated;
    this.lastRemovedCards = lastRemovedCards;
    this.game = game;
    this.eventLog = eventLog;
    this.board = new Board(boardId);
    this.visibility = visibility;
    this.allowSpectators = allowSpectators;
    this.chatVisibility = chatVisibility;
  }

  public getType() {
    return ControllerType.LOCAL;
  }

  public getGame() {
    return this.game;
  }

  public setEventLog(eventLog: EventLogEntry[]): void {
    this.eventLog = eventLog;
  }

  public getEventLog(): EventLogEntry[] {
    return this.eventLog;
  }

  public getBoard() {
    return this.board;
  }

  /**
   * ReceiveMsg
   * @method Turfmaster.Controller#receiveMsg
   * @param {Turfmaster.stateMachine} state - State
   * @param {Array} params - Params
   */
  public async receiveMsg(state: StateMachineValue, params: any): Promise<false | void> {
    switch (state) {
      case Turfmaster.stateMachine.NEWGAME:
        await this.newGameEvent(params);
        break;
      case Turfmaster.stateMachine.JOINGAME:
      case Turfmaster.stateMachine.JOINTOURNAMENT:
        await this.joinGameEvent(params);
        break;
      case Turfmaster.stateMachine.MERGECARDS:
      case Turfmaster.stateMachine.ADDBONUSCARDS:
      case Turfmaster.stateMachine.SELECTCARDDICE:
      case Turfmaster.stateMachine.SELECTMOVE:
        // validate state and parameters
        if (!this.validateState(state)) {
          gameLogger.error(
            {
              userId: params?.userid,
              currentState: this.game.currentState,
              receivedState: state,
              stateName: stateToString(state),
            },
            'error invalid state',
          );
          return false;
        } else if (params.playerid !== this.getPlayerId()) {
          gameLogger.error(
            `playerid ${params.playerid} invalid, only current playerid ${this.getPlayerId()} is allowed`,
          );
          return false;
        } else if (params.horseid !== this.getHorseId()) {
          gameLogger.error(
            `horseid ${params.horseid} invalid, only current horseid ${this.getHorseId()} is allowed`,
          );
          return false;
        }

        // act on player action
        switch (state) {
          case Turfmaster.stateMachine.MERGECARDS:
            await this.mergeCardsEvent(params);
            break;
          case Turfmaster.stateMachine.ADDBONUSCARDS:
            await this.addBonusCardsEvent(params);
            break;
          case Turfmaster.stateMachine.SELECTCARDDICE:
            await this.selectCardDiceEvent(params);
            break;
          case Turfmaster.stateMachine.SELECTMOVE:
            await this.selectMoveEvent(params);
            break;
        }
        break;
      case Turfmaster.stateMachine.SELECTDICES:
        // validate state and parameters
        if (!this.validateState(state)) {
          gameLogger.error(
            {
              userId: params?.userid,
              currentState: this.game.currentState,
              receivedState: state,
              stateName: stateToString(state),
            },
            'error invalid state',
          );
          return false;
        } else if (params.playerid !== this.game.turndiceplayerid) {
          gameLogger.error(
            `playerid ${params.playerid} invalid, only current turndiceplayerid ${this.game.turndiceplayerid} is allowed`,
          );
          return false;
        }

        await this.selectDicesEvent(params.dicesid, params.userid);
        break;
      case Turfmaster.stateMachine.NEWMESSAGE:
        await this.newMessageEvent(params.message, params.userid, params.senderName);
        break;
      case Turfmaster.stateMachine.LEAVEGAME:
        return await this.leaveGameEvent(params.playerid, params.userid);
    }
  }

  /**
   * SendMsg
   * @method Turfmaster.Controller#sendMsg
   * @param {Turfmaster.stateMachine} state - State
   * @param {Object} params - Params
   */
  public async sendMsg(state: StateMachineValue, params: any): Promise<false | void> {
    this.updateState(state);

    switch (state) {
      case Turfmaster.stateMachine.NEWGAME:
      case Turfmaster.stateMachine.JOINGAME:
        if (Object.prototype.hasOwnProperty.call(params, 'playerid')) {
          if (params.playerid === -999) {
            break;
          }

          if (!this.playerViews[params.playerid]) {
            gameLogger.error('error invalid playerid parameter');
            return false;
          }

          await this.playerViews[params.playerid].receiveMsg(state, params);
        } else {
          // JOINGAME not possible: notify view about this
          const view = params.view;
          delete params.view;
          await view.receiveMsg(state, params);
        }
        break;
      case Turfmaster.stateMachine.JOINGAMEOPP:
      case Turfmaster.stateMachine.STARTGAME:
      case Turfmaster.stateMachine.STARTROUND:
      case Turfmaster.stateMachine.MERGECARDS:
      case Turfmaster.stateMachine.MERGECARDSOPP:
      case Turfmaster.stateMachine.STARTTURN:
      case Turfmaster.stateMachine.ADDBONUSCARDSOPP:
      // SKIPPLAYER: Informational event broadcast to all players when a player is skipped due to no valid moves.
      // Unlike player action events (ADDBONUSCARDS, SELECTMOVE), this is sent directly by the controller.
      // All players receive this event so they can see in history/chat that a player was skipped.
      // Event is displayed using translation key 'event_player_skipped' with {{player}} and {{horse}} placeholders.
      // This event is logged to eventLog (not transient like SELECTMOVE/SELECTCARDDICE) for history/replay.
      // eslint-disable-next-line no-fallthrough -- intentional: SKIPPLAYER shares handler with SELECTCARDDICE
      case Turfmaster.stateMachine.SKIPPLAYER:
      case Turfmaster.stateMachine.SELECTCARDDICE:
      case Turfmaster.stateMachine.SELECTMOVE:
      case Turfmaster.stateMachine.SELECTMOVEOPP:
      case Turfmaster.stateMachine.SELECTDICES:
      case Turfmaster.stateMachine.SELECTDICESOPP:
      case Turfmaster.stateMachine.ENDTURN:
      case Turfmaster.stateMachine.ENDROUND:
      case Turfmaster.stateMachine.ENDGAME:
      case Turfmaster.stateMachine.NEWMESSAGE:
      case Turfmaster.stateMachine.LEAVEGAMEOPP:
        for (let i = 0; i < this.game.players.length; i++) {
          if (!this.playerViews[i]) {
            gameLogger.error(`error playerid ${i} not found`);
            return false;
          }

          await this.playerViews[i].receiveMsg(state, params);
        }
        break;
    }
  }

  /**
   * NewGameEvent
   * @method Turfmaster.Controller#newGameEvent
   * @param newGameParams
   * @param {Turfmaster.ViewLocal} view - View
   */
  public async newGameEvent(newGameParams: NewGameRequest) {
    if (newGameParams.userid === undefined || newGameParams.userid === '') {
      gameLogger.error('error newGameEvent: userid is empty');
      return false;
    }

    this.createBoard();

    // playerid should be > 0 for valid values where a game is created and joined immediately
    // playerid is -999 for a game without a player
    const playerid = this.createGame(newGameParams);

    if (playerid < 0 && playerid !== -999) {
      gameLogger.error('error createGame');
      return playerid;
    }

    await this.sendMsg(Turfmaster.stateMachine.NEWGAME, {
      playerid: playerid,
      gameid: this.gameid,
      ...newGameParams,
    });

    if (!this.isHorseLeft()) {
      await this.startGame();
    }
  }

  public async joinGameEvent(joinGameParams: JoinGameRequest) {
    if (joinGameParams.userid === undefined || joinGameParams.userid === '') {
      gameLogger.error('error addPlayer: userid is empty');
    }

    const playerid = this.addPlayer(
      joinGameParams.player,
      joinGameParams.view,
      joinGameParams.userid,
    );

    if (playerid < 0) {
      gameLogger.error('error addPlayer');
      await this.sendMsg(Turfmaster.stateMachine.JOINGAME, {
        error: playerid,
        view: joinGameParams.view,
      });
      return playerid;
    }

    // sending eventLog here to have the client receive it immediately
    await this.sendMsg(Turfmaster.stateMachine.JOINGAME, {
      playerid: playerid,
      eventLog: this.eventLog,
      ...joinGameParams,
    });
    // Include eventLog in JOINGAMEOPP params so existing players get updated log
    await this.sendMsg(Turfmaster.stateMachine.JOINGAMEOPP, {
      playerid: playerid,
      eventLog: this.eventLog,
      ...joinGameParams,
    });

    if (!this.isHorseLeft()) {
      await this.startGame();
    }
  }

  /**
   * MergeCardsEvent
   * @method Turfmaster.Controller#mergeCardsEvent
   * @param params
   */
  public async mergeCardsEvent(params: any) {
    if (params.userid === undefined || params.userid === '') {
      gameLogger.error('error mergeCardsEvent: userid is empty');
      return false;
    }

    if (params.horseid === undefined) {
      gameLogger.error('error mergeCardsEvent: horseid is missing');
      return false;
    }

    if (params.added === undefined || !(params.added instanceof Array)) {
      gameLogger.error('error mergeCardsEvent: added is missing or invalid');
      return false;
    }

    if (params.removed === undefined || !(params.removed instanceof Array)) {
      gameLogger.error('error mergeCardsEvent: removed is missing or invalid');
      return false;
    }

    if (!this.mergeCards(params.horseid, params.added, params.removed)) {
      gameLogger.error('error mergeCards');
      return false;
    }

    await this.sendMsg(Turfmaster.stateMachine.MERGECARDSOPP, { userid: params.userid });

    this.game.turnpos++;

    if (this.game.turnpos === this.game.turnorder.length) {
      this.calcRanks();
      this.calcHandi();
      this.calcTurnorder();

      await this.startTurn();
    } else {
      this.lastRemovedCards = this.updateBonusCards(this.getHorseId());
      await this.sendMsg(Turfmaster.stateMachine.MERGECARDS, {
        userid: params.userid,
        removedCards: this.lastRemovedCards,
      });
    }
  }

  /**
   * AddBonusCardsEvent
   * @method Turfmaster.Controller#addBonusCardsEvent
   * @param params
   */
  public async addBonusCardsEvent(params: any) {
    if (params.userid === undefined || params.userid === '') {
      gameLogger.error('error addBonusCardsEvent: userid is empty');
      return false;
    }

    if (params.horseid === undefined) {
      gameLogger.error('error addBonusCardsEvent: horseid is missing');
      return false;
    }

    if (!this.addBonusCards(params.horseid)) {
      gameLogger.error('error addBonusCards');
      return false;
    }

    if (!this.game.horses[params.horseid]) {
      gameLogger.error('error horse invalid');
      return false;
    }

    const lastState = this.game.currentState;

    await this.sendMsg(Turfmaster.stateMachine.ADDBONUSCARDSOPP, {
      userid: params.userid,
      horseid: params.horseid,
    });

    if (lastState === Turfmaster.stateMachine.MERGECARDS) {
      await this.sendMsg(Turfmaster.stateMachine.MERGECARDS, {
        userid: params.userid,
        removedCards: this.lastRemovedCards,
      });
    } else if (lastState === Turfmaster.stateMachine.SELECTCARDDICE) {
      await this.sendMsg(Turfmaster.stateMachine.SELECTCARDDICE, { userid: params.userid });
    } else if (lastState === Turfmaster.stateMachine.SELECTMOVE) {
      const horseid = this.getHorseId();

      const moves = this.getMovesLogic(horseid, -1, false);

      if (!moves) {
        gameLogger.error('error getMovesLogic');
        return false;
      }

      if (moves.length > 0) {
        await this.sendMsg(Turfmaster.stateMachine.SELECTMOVE, {
          userid: params.userid,
          moves: moves,
          carddice: -1,
        });
      } else {
        await this.sendMsg(Turfmaster.stateMachine.SELECTCARDDICE, { userid: params.userid });
      }
    } else if (lastState === Turfmaster.stateMachine.SELECTDICES) {
      await this.sendMsg(Turfmaster.stateMachine.SELECTDICES, { userid: params.userid });
    }
  }

  /**
   * SelectCardDiceEvent
   * @method Turfmaster.Controller#selectCardDiceEvent
   * @param params
   */
  public async selectCardDiceEvent(params: any) {
    if (params.userid === undefined || params.userid === '') {
      gameLogger.error('error selectCardDiceEvent: userid is empty');
      return false;
    }

    if (params.horseid === undefined) {
      gameLogger.error('error selectCardDiceEvent: horseid is missing');
      return false;
    }

    if (params.carddiceid === undefined) {
      gameLogger.error('error selectCardDiceEvent: carddiceid is missing');
      return false;
    }

    const moves = this.getMovesLogic(params.horseid, params.carddiceid, true);

    if (!moves) {
      gameLogger.error('error getMovesLogic');
      return false;
    }

    const carddice = this.getCardDice(params.horseid, params.carddiceid);

    if ((moves as Move[]).length > 0) {
      await this.sendMsg(Turfmaster.stateMachine.SELECTMOVE, {
        userid: params.userid,
        moves: moves,
        carddice: carddice,
      });
    } else {
      await this.sendMsg(Turfmaster.stateMachine.SELECTCARDDICE, { userid: params.userid });
    }
  }

  /**
   * SelectMoveEvent
   * @method Turfmaster.Controller#selectMoveEvent
   * @param params
   */
  public async selectMoveEvent(params: any) {
    if (params.userid === undefined || params.userid === '') {
      gameLogger.error('error selectMoveEvent: userid is empty');
      return false;
    }

    if (params.horseid === undefined) {
      gameLogger.error('error selectMoveEvent: horseid is missing');
      return false;
    }

    if (params.carddiceid === undefined) {
      gameLogger.error('error selectMoveEvent: carddiceid is missing');
      return false;
    }

    if (!this.game.horses[params.horseid]) {
      gameLogger.error('error horse invalid');
      return false;
    }

    const horse: Horse = this.game.horses[params.horseid];

    const moves = this.getMovesLogic(params.horseid, params.carddiceid, false);

    // No moves available for this card/dice - handle based on turn type.
    // Card turns: retry another card (send SELECTCARDDICE again), matching selectCardDiceEvent behavior.
    // Dice turns: advance turn (dice is fixed by throwing player, cannot retry).
    // Bot may send SELECTMOVE with moveid: 0 when moves.length === 0; handle gracefully instead of rejecting.
    if (!moves || moves.length === 0) {
      if (this.game.turntype === Turfmaster.turntypes.CARDS) {
        // Card turn: retry another card (same behavior as selectCardDiceEvent line 460)
        await this.sendMsg(Turfmaster.stateMachine.SELECTCARDDICE, { userid: params.userid });
        return;
      } else {
        // Dice turn: advance turn (dice is fixed, cannot retry)
        this.game.turnpos++;
        await this.setNextMove();
        return;
      }
    }

    // Validate and normalize moveid: handle undefined/null/NaN from bot (e.g., MEDIUM strength with empty movesSelected).
    // Default to moveid 0 if moves exist; reject only if no moves available (handled above).
    let moveid = params.moveid;
    if (moveid === undefined || moveid === null || (typeof moveid === 'number' && isNaN(moveid))) {
      if (moves && moves[0]) {
        moveid = 0;
      } else {
        gameLogger.error('error selectMoveEvent: moveid is missing');
        return false;
      }
    }
    // Validate moveid is within bounds; fallback to 0 if out of range but moves exist.
    if (!moves || !moves[moveid]) {
      if (moves && moves[0]) {
        moveid = 0;
      } else {
        gameLogger.error('error getMovesLogic');
        return false;
      }
    }

    const move = moves[moveid];

    const carddice: boolean | number = this.getCardDice(params.horseid, params.carddiceid);

    if (!carddice && carddice !== 0) {
      return false;
    }

    if (this.game.turntype === Turfmaster.turntypes.CARDS) {
      this.playCard(params.horseid, params.carddiceid);
    }

    if (move.drop) {
      this.removeHorse(params.horseid, false, true);
    } else {
      const moved = this.moveHorse(params.horseid, move.fields[move.fields.length - 1]);
      if (moved === false) {
        gameLogger.error('error selectMoveEvent: moveHorse blocked');
        return false;
      }
    }

    const handicapExceeded =
      this.game.turntype === Turfmaster.turntypes.CARDS
        ? this.isHandi(params.horseid, carddice)
        : false;
    await this.sendMsg(Turfmaster.stateMachine.SELECTMOVEOPP, {
      userid: params.userid,
      horseid: params.horseid,
      move,
      carddice,
      handicapExceeded,
    });

    if (!horse.field) {
      this.game.turnorder.splice(this.game.turnpos, 1);
    } else {
      this.game.turnpos++;
    }

    await this.setNextMove();
  }

  /**
   * SelectDicesEvent
   * @method Turfmaster.Controller#selectDicesEvent
   * @param {Number} dicesid - Dicesid
   */
  public async selectDicesEvent(dicesid: any, userid: string) {
    if (dicesid < 0 || dicesid > this.game.turndices.length) {
      gameLogger.error('error invalid dicesid');
      return false;
    }

    this.game.turndicescur = dicesid;

    await this.sendMsg(Turfmaster.stateMachine.SELECTDICESOPP, { userid: userid });

    await wait(sampleBotThinkDelayMs(this.pacingProfile, 'diceResultDisplay'));

    await this.setNextMove();
  }

  public async newMessageEvent(message: string, userId: string, senderName?: string | null) {
    // EventLog entry will be created automatically in ControllerLocalProxy.sendMsg
    await this.sendMsg(Turfmaster.stateMachine.NEWMESSAGE, {
      message: message,
      senderId: userId,
      timestamp: dayjs().utc().toISOString(),
      userid: userId,
      senderName:
        typeof senderName === 'string' && senderName.trim().length > 0 ? senderName.trim() : null,
    });
  }

  /**
   * LeaveGameEvent
   * @method Turfmaster.Controller#leaveGameEvent
   * @param {Number} playerid - Player ID of the player leaving
   */
  public async leaveGameEvent(playerid: number, userid: string): Promise<false | void> {
    // Block if game already started
    if (this.game.round > 0) {
      gameLogger.info('TODO: Replace player with bot when game is running');
      return false;
    }

    // Validate playerid
    if (playerid < 0 || playerid >= this.game.players.length) {
      gameLogger.error({ playerid }, 'error leaveGameEvent: invalid playerid');
      return false;
    }

    // Notify leaving player first (before removing them)
    const leavingPlayerView = this.playerViews[playerid];
    if (leavingPlayerView) {
      await leavingPlayerView.receiveMsg(Turfmaster.stateMachine.LEAVEGAME, {
        playerid: playerid,
        userid: userid,
      });
    }

    // Remove all horses belonging to this player
    const horsesToRemove: number[] = [];
    for (let i = this.game.horses.length - 1; i >= 0; i--) {
      if (this.game.horses[i].playerid === playerid) {
        horsesToRemove.push(i);
      }
    }

    // Remove horses in reverse order to maintain indices
    for (const horseIndex of horsesToRemove) {
      this.game.horses.splice(horseIndex, 1);
    }

    // Remove player from players array
    this.game.players.splice(playerid, 1);

    // Remove playerView
    delete this.playerViews[playerid];

    // Adjust player IDs for remaining players (shift indices)
    // Update horses' playerid references
    for (let i = 0; i < this.game.horses.length; i++) {
      if (this.game.horses[i].playerid > playerid) {
        this.game.horses[i].playerid--;
      }
    }

    // Shift playerViews array to fill the gap
    const newPlayerViews: any[] = [];
    for (let i = 0; i < this.game.players.length; i++) {
      if (i < playerid) {
        newPlayerViews[i] = this.playerViews[i];
      } else if (i >= playerid) {
        newPlayerViews[i] = this.playerViews[i + 1];
        // Update playerid in view if it has one
        if (newPlayerViews[i] && typeof newPlayerViews[i].playerid === 'number') {
          newPlayerViews[i].playerid = i;
        }
      }
    }
    this.playerViews = newPlayerViews;

    // Notify other players
    await this.sendMsg(Turfmaster.stateMachine.LEAVEGAMEOPP, {
      playerid: playerid,
      userid: userid,
    });
  }

  /**
   * StartGameEvent
   * @method Turfmaster.Controller#startGame
   */
  public async startGame() {
    this.game.turndiceplayerid = randomInt(0, this.game.players.length - 1);

    this.shuffle(this.game.horses);

    // Board has a fixed number of lanes (8). Map each horse to a lane without assuming
    // horse count equals lane count; otherwise fields[i] is undefined when i >= fields.length.
    const numLanes = this.board.fields.length;
    for (let i = 0; i < this.game.horses.length; i++) {
      const laneIndex = i % numLanes;
      const startField = this.board.fields[laneIndex][0];
      if (startField) {
        this.moveHorse(i, startField);
      }
    }

    this.calcRanks();
    this.calcHandi();
    this.calcTurnorder();

    await this.sendMsg(Turfmaster.stateMachine.STARTGAME, {});

    await this.startRound();
  }

  /**
   * StartRoundEvent
   * @method Turfmaster.Controller#startRound
   */
  public async startRound() {
    // reset 'dropped' and 'finished' properties for each new round
    this.game.horses.forEach((horse) => {
      horse.dropped = false;
      horse.finished = false;
    });

    await this.sendMsg(Turfmaster.stateMachine.STARTROUND, { round: this.game.round });

    if (this.game.round > 0) {
      const removedCards = this.updateBonusCards(this.getHorseId());
      await this.sendMsg(Turfmaster.stateMachine.MERGECARDS, { removedCards: removedCards });
    } else {
      await this.startTurn();
    }
  }

  /**
   * StartTurnEvent
   * @method Turfmaster.Controller#startTurn
   */
  public async startTurn() {
    await this.sendMsg(Turfmaster.stateMachine.STARTTURN, { turntype: this.game.turntype });

    if (this.game.turntype === Turfmaster.turntypes.DICES) {
      this.calcTurndices();

      await this.sendMsg(Turfmaster.stateMachine.SELECTDICES, {});
    } else {
      await this.setNextMove();
    }
  }

  /**
   * SetNextMove
   * @method Turfmaster.Controller#setNextMove
   */
  public async setNextMove(): Promise<void | boolean> {
    // check if this call is the final move in the turn order
    if (this.game.turnpos === this.game.turnorder.length) {
      const horses = this.game.horses;
      const finishedhorseids: number[] = [];

      for (let i = 0; i < horses.length; i++) {
        if (this.isGoal(i) && !horses[i].finished) {
          this.calcPoints(i);
          finishedhorseids.push(i);
        }
      }

      for (let i = 0; i < horses.length; i++) {
        if (this.isGoal(i) && !horses[i].finished) {
          this.removeHorse(i, true, false);
        }
      }

      this.calcRanks();
      this.calcHandi();
      this.calcTurnorder();

      // check if any horses finished
      if (finishedhorseids.length > 0) {
        // wait for the timeout before ending the turn
        await wait(Turfmaster.finishedHorsesTurnTimeout);
        await this.endTurn(finishedhorseids);
      } else {
        // no horses finished, end turn immediately
        await this.endTurn(finishedhorseids);
      }
    } else {
      const horseid = this.getHorseId();

      // skip horses that are already in the goal
      if (this.isGoal(horseid)) {
        this.game.turnpos++;
        return await this.setNextMove();
      }

      const moves = this.getMovesLogic(horseid, -1, false);

      if (!moves) {
        gameLogger.error('error getMovesLogic');
        return false;
      }

      // getMovesLogic with carddiceid === -1 always returns [] as a placeholder to prompt card/dice selection.
      // Check if horse actually has any valid moves by checking all cards/dice.
      // If no card/dice has valid moves, skip player to prevent infinite loop.
      // This handles cases where all cards have no moves (e.g., all would drop the horse).
      // Without this safeguard, the game would loop indefinitely: setNextMove sends SELECTCARDDICE,
      // bot selects a card and sends SELECTMOVE with moveid: 0, selectMoveEvent retries SELECTCARDDICE, repeat.
      // Similar to goal check: if horse cannot move, skip to next player.
      if (!this.hasAnyValidMoves(horseid)) {
        // Send SKIPPLAYER event to notify all players that this player was skipped.
        // This event is informational only (like ADDBONUSCARDSOPP) and gets broadcast to all players via sendMsg().
        // Event flow:
        //   1. Controller detects no valid moves → calls sendMsg(SKIPPLAYER, ...)
        //   2. sendMsg() routes SKIPPLAYER to broadcast case (line 222)
        //   3. Broadcast case sends receiveMsg(SKIPPLAYER, ...) to all player views
        //   4. Each client receives the event and displays it in history/chat
        // The event payload includes player info so frontend can display: "{{player}}'s {{horse}} was skipped (no valid moves)"
        // Note: SKIPPLAYER is NOT a transient event (unlike SELECTMOVE/SELECTCARDDICE) - it's logged to eventLog
        // for history/replay, similar to ADDBONUSCARDSOPP.
        const playerId = this.getPlayerId();
        const player =
          typeof playerId === 'number' && playerId >= 0 ? this.game.players[playerId] : null;
        await this.sendMsg(Turfmaster.stateMachine.SKIPPLAYER, {
          userid: USERID_FOR_SERVER_MESSAGES, // System message, not from a player
          playerid: typeof playerId === 'number' ? playerId : -1, // Player who was skipped
          playerName: player?.name || 'Unknown', // Player name for display
          horseid: horseid, // Horse that was skipped (frontend resolves horse name from this)
        });
        this.game.turnpos++;
        return await this.setNextMove();
      }

      if (moves.length > 0) {
        await this.sendMsg(Turfmaster.stateMachine.SELECTMOVE, { moves: moves, carddice: -1 });
      } else {
        await this.sendMsg(Turfmaster.stateMachine.SELECTCARDDICE, {});
      }
    }
  }

  /**
   * GetCardDice
   * @method Turfmaster.Controller#getCardDice
   * @param {Number} horseid - Horseid
   * @param {Number} carddiceid - Carddiceid
   */
  public getCardDice(horseid: number, carddiceid: number) {
    let carddice: number | boolean;

    if (this.game.turntype === Turfmaster.turntypes.CARDS) {
      const cards = this.getCurrentCards(horseid);

      // If horse has no cards for current round, let it stay it current position
      if (cards && cards.length === 0) {
        return 0;
      }

      if (carddiceid === -1) {
        return -1;
      }

      if (!cards || !cards[carddiceid]) {
        gameLogger.error('error getCurrentCards invalid');
        return false;
      }

      carddice = cards[carddiceid];
    } else {
      carddice = this.calcDicessum();

      if (!carddice) {
        gameLogger.error('error calcDicessum carddice not set');
        return false;
      }

      if (this.isHandi(horseid, carddice)) {
        const dices = this.getDices();

        // If dices are identical use one of them to skip select of dice
        if (dices[0] === dices[1]) {
          carddiceid = 0;
        }

        if (carddiceid === -1) {
          return carddiceid;
        }

        if (!dices || !dices[carddiceid]) {
          gameLogger.error('error getDices invalid dices');
          return false;
        }

        carddice = dices[carddiceid];
      }
    }

    return carddice;
  }

  /**
   * EndTurnEvent
   * @method Turfmaster.Controller#endTurn
   * @param {Array} finishedhorseids - Finishedhorseids
   */
  public async endTurn(finishedhorseids: number[]) {
    await this.sendMsg(Turfmaster.stateMachine.ENDTURN, { finishedhorseids: finishedhorseids });

    let foundHorse = false;
    for (let i = 0; i < this.game.horses.length; i++) {
      if (this.game.horses[i].field) {
        foundHorse = true;
        break;
      }
    }

    if (this.game.turntype === Turfmaster.turntypes.CARDS) {
      this.game.turntype = Turfmaster.turntypes.DICES;

      if (foundHorse) {
        let foundTurndiceplayer = false;

        do {
          this.game.turndiceplayerid++;
          if (this.game.turndiceplayerid === this.game.players.length) {
            this.game.turndiceplayerid = 0;
          }

          // Check if turndiceplayer has horses in current round
          for (let i = 0; i < this.game.horses.length; i++) {
            if (
              this.game.horses[i].playerid === this.game.turndiceplayerid &&
              this.game.horses[i].field
            ) {
              foundTurndiceplayer = true;
              break;
            }
          }
        } while (!foundTurndiceplayer);
      }
    } else {
      this.game.turntype = Turfmaster.turntypes.CARDS;
    }

    if (!foundHorse) {
      await this.endRound();
    } else {
      await this.startTurn();
    }
  }

  /**
   * EndRoundEvent
   * @method Turfmaster.Controller#endRound
   */
  public async endRound() {
    await this.sendMsg(Turfmaster.stateMachine.ENDROUND, { round: this.game.round });

    this.calcTurnorder(true);

    for (let i = 0; i < this.game.turnorder.length; i++) {
      this.moveHorse(this.game.turnorder[i], this.board.fields[i][0]);
    }

    this.calcRanks();
    this.calcTotalRanks();
    this.calcHandi();
    this.calcTurnorder();

    this.game.round++;
    this.game.turntype = Turfmaster.turntypes.CARDS;

    // wait before starting the next round
    await wait(Turfmaster.endRoundTimeout);
    if (this.game.round === Turfmaster.rounds) {
      await this.endGame();
    } else {
      await this.startRound();
    }
  }

  /**
   * EndGameEvent
   * @method Turfmaster.Controller#endGame
   */
  public async endGame() {
    await this.sendMsg(Turfmaster.stateMachine.ENDGAME, {});
  }

  /**
   * UpdateState
   * @method Turfmaster.Controller#updateState
   * @param {number} state - State
   */
  public updateState(state: StateMachineValue) {
    // message events are only transient and should not change the internal statemachine state
    if (
      state === Turfmaster.stateMachine.NEWMESSAGE ||
      state === Turfmaster.stateMachine.SENDMESSAGE ||
      state === Turfmaster.stateMachine.LEAVEGAME ||
      state === Turfmaster.stateMachine.LEAVEGAMEOPP ||
      state === Turfmaster.stateMachine.ADDBONUSCARDSOPP
    ) {
      return;
    }

    this.game.currentState = state;
  }

  /**
   * ValidateState
   * @method Turfmaster.Controller#validateState
   * @param {Turfmaster.stateMachine} state - State
   */
  public validateState(state: StateMachineValue) {
    const states = Turfmaster.stateMatrix[this.game.currentState as StateMatrixKey];
    return states && states.indexOf(state) !== -1;
  }

  public createBoard() {
    this.board = new Board(0); // tbd boardid

    return true;
  }

  /**
   * CreateGame
   * @method Turfmaster.Controller#createGame
   * @param newGame
   * @param {Turfmaster.ViewLocal} view - View
   */
  public createGame(newGame: NewGameRequest) {
    this.playerViews = [];

    this.pacingProfile = newGame.pacingProfile ?? BOT_PACING_LOCAL_TRAINING;

    this.game = new Game();

    // Ensure local (training) games always have a stable, non-empty id so client snapshot
    // validation/restoration can roundtrip across refresh.
    this.gameid = this.gameid && this.gameid.length > 0 ? this.gameid : uuid();
    // Keep timecreated consistent with the currently created game.
    this.timecreated = dayjs().utc().toISOString();

    this.game.name = newGame.gameName;

    if (newGame.visibility !== undefined) {
      this.visibility = newGame.visibility === 'public' ? 'public' : 'private';
    }
    if (newGame.allowSpectators !== undefined) {
      this.allowSpectators = newGame.allowSpectators;
    }
    if (newGame.chatVisibility !== undefined) {
      this.chatVisibility = newGame.chatVisibility === 'public' ? 'public' : 'playersonly';
    }

    // Cap horseamount to Turfmaster.maxHorses (8). The board has only 8 lanes; startGame()
    // places each horse on board.fields[laneIndex][0]. If horseamount > 8, that would
    // access undefined (e.g. fields[8]) and crash. Capping here ensures addPlayer/addComputer
    // reject excess horses and we never exceed lane count.
    this.game.horseamount = Math.min(newGame.horseamount, Turfmaster.maxHorses);

    let playerid = -999;

    if (newGame.player !== undefined) {
      // ViewRemote.sendMsg sets params.view before calling controller.receiveMsg
      // NewGameRequest includes view?: any, so we can access it directly
      playerid = this.addPlayer(
        newGame.player,
        newGame.view,
        newGame.userid ?? USERID_FOR_SERVER_MESSAGES,
      );

      if (playerid < 0) {
        gameLogger.error('error addPlayer invalid playerid');
        return playerid;
      }
    }

    if (newGame.computers !== undefined) {
      for (let i = 0; i < newGame.computers.length; i++) {
        const addComputerReturn = this.addComputer(newGame.computers[i]);
        if (addComputerReturn < 0) {
          gameLogger.error('error addComputer');
          return addComputerReturn;
        }
      }
    }

    return playerid;
  }

  /**
   * CreatePlayers
   * @method Turfmaster.Controller#createPlayers
   * @param player
   * @param view
   * @param userid
   */
  public addPlayer(player: PlayerProp, view: any, userid: string) {
    if (this.game.horses.length + player.horses.length > this.game.horseamount) {
      gameLogger.error('error player horses array invalid');
      return -1;
    }

    const tmpplayer = new Player();
    this.game.players.push(tmpplayer);
    const playerid = this.game.players.length - 1;

    tmpplayer.userid = userid;
    tmpplayer.name = player.name ?? this.randomNames.getRandomStudFarmName();
    tmpplayer.country = player.country ?? this.randomNames.getRandomCountryCode();
    tmpplayer.type = Turfmaster.playerTypes.PLAYER;

    this.playerViews[playerid] = view;

    const addHorsesReturn = this.addHorses(playerid, player.horses);
    if (addHorsesReturn < 0) {
      gameLogger.error('error addHorses');
      return addHorsesReturn;
    }

    return playerid;
  }

  public addComputer(player: ComputerProp) {
    if (this.game.horses.length + player.horses.length > this.game.horseamount) {
      gameLogger.error('error player horses array invalid');
      return -2;
    }

    const tmpplayer = new Player();
    this.game.players.push(tmpplayer);
    const playerid = this.game.players.length - 1;

    tmpplayer.userid = player.userid ?? uuid();
    tmpplayer.name = player.name ?? this.randomNames.getRandomStudFarmName();
    tmpplayer.country = player.country ?? this.randomNames.getRandomCountryCode();
    tmpplayer.type = Turfmaster.playerTypes.COMPUTER;

    const botPacing = this.resolveComputerPacingProfile(player);
    switch (player.strength ?? Turfmaster.compStrengths.MEDIUM) {
      case Turfmaster.compStrengths.EASY:
        this.playerViews[playerid] = new Computer(
          playerid,
          Turfmaster.compStrengths.EASY,
          this,
          botPacing,
        );
        break;
      case Turfmaster.compStrengths.MEDIUM:
        this.playerViews[playerid] = new Computer(
          playerid,
          Turfmaster.compStrengths.MEDIUM,
          this,
          botPacing,
        );
        break;
      case Turfmaster.compStrengths.STRONG:
        this.playerViews[playerid] = new Computer(
          playerid,
          Turfmaster.compStrengths.STRONG,
          this,
          botPacing,
        );
        break;
    }

    const addHorsesReturn = this.addHorses(playerid, player.horses);
    if (addHorsesReturn < 0) {
      gameLogger.error('error addHorses');
      return addHorsesReturn;
    }

    return playerid;
  }

  public replacePlayerWithComputer(
    playerid: number,
    strength: number = Turfmaster.compStrengths.MEDIUM,
    pacingProfile = BOT_PACING_LOCAL_TRAINING,
  ) {
    // userid, name, horses and type stays the same, only the view is updated
    this.playerViews[playerid] = new Computer(playerid, strength, this, pacingProfile);

    return this.playerViews[playerid];
  }

  private resolveComputerPacingProfile(player: ComputerProp) {
    if (player.pacingProfile) {
      return player.pacingProfile;
    }
    if (player.delay !== undefined) {
      return pacingProfileFromBaseDelayMs(player.delay);
    }
    return BOT_PACING_LOCAL_TRAINING;
  }

  /**
   * CreateHorses
   * @method Turfmaster.Controller#createHorses
   * @param playerid
   * @param horses
   */
  public addHorses(playerid: number, horses: HorseProp[]) {
    for (let i = 0; i < horses.length; i++) {
      // TODO Check for valid horseid
      if (horses[i].horseid === '') {
        gameLogger.error('error horseid invalid');
        return -4;
      }
      // TODO Check for valid cardtype
      if (horses[i].cardtype === -1) {
        gameLogger.error('error cardtype invalid');
        return -5;
      }
    }

    const rounds = Turfmaster.rounds;
    const cardsround = 10; // Math.floor(defcards.length / rounds);

    for (let i = 0; i < horses.length; i++) {
      const horse = new Horse();
      this.game.horses.push(horse);

      horse.horseid = horses[i].horseid ?? uuid();
      horse.playerid = playerid;
      horse.name = horses[i].name ?? this.randomNames.getRandomHorseName();
      horse.color = horses[i].color ?? -1;
      horse.jockeyName = horses[i].jockeyName ?? this.randomNames.getRandomJockeyName();
      // always enforce gold card deck
      horse.cardType = Turfmaster.cardTypes.GOLD;

      const cards: number[][] = [];
      horse.cards = cards;
      // always enforce gold card deck
      const defcards = Turfmaster.cardsGold.slice();
      this.shuffle(defcards);
      for (let k = 0; k < rounds; k++) {
        cards[k] = [];
        for (let l = 0; l < cardsround; l++) {
          cards[k][l] = defcards[k * cardsround + l];
        }
        cards[k] = cards[k].slice().sort((a, b) => {
          return a - b;
        });
      }
      cards.push([]);
      for (let k = 0; k < defcards.length - rounds * cardsround; k++) {
        cards[cards.length - 1][k] = defcards[rounds * cardsround + k];
      }
      cards[cards.length - 1] = cards[cards.length - 1].slice().sort((a, b) => {
        return a - b;
      });
    }

    return 0;
  }

  public getRanksOfRound(round: number): readonly number[] {
    const horses = this.game.horses;
    const ranks: number[] = [];

    for (let i = 0; i < horses.length; i++) {
      if (horses[i].ranks === undefined) {
        horses[i].ranks = [];
      }

      if (horses[i].ranks[round] !== undefined) {
        ranks.push(horses[i].ranks[round]);
      } else if (round === this.game.round) {
        ranks.push(horses[i].rank);
      } else {
        ranks.push(horses.length - 1);
      }
    }

    return ranks;
  }

  public getRanksOfHorse(horseid: number): readonly number[] {
    const horses = this.game.horses;
    const ranks: number[] = [];

    for (let i = 0; i < horses.length; i++) {
      ranks[i] = 0;
    }

    for (let i = 0; i < Turfmaster.rounds; i++) {
      if (horses[horseid].ranks === undefined) {
        horses[horseid].ranks = [];
      }

      if (horses[horseid].ranks[i] !== undefined) {
        ranks[horses[horseid].ranks[i]]++;
      } else if (i === this.game.round) {
        ranks[horses[horseid].rank]++;
      } else {
        ranks[horses.length - 1]++;
      }
    }

    return ranks;
  }

  /**
   * CalcRanks
   * @method Turfmaster.Controller#calcRanks
   */
  public calcRanks() {
    const horses = this.game.horses;
    const tmpFields: Field[] = [];
    const tmpRanks: number[] = [];
    let goalHorses = 0;

    for (let i = 0; i < horses.length; i++) {
      const field = horses[i].field;
      if (field) {
        tmpFields[i] = field;
      } else if (this.isGoal(i)) {
        tmpRanks[i] = horses[i].ranks[this.game.round];
        goalHorses++;
      }
    }

    const _this = this;
    const sorted = tmpFields.slice().sort(function (a, b) {
      return _this.compareFields(a, b);
    });
    const ranks = tmpFields.slice().map(function (v) {
      return sorted.indexOf(v);
    });
    const goalSorted = tmpRanks.slice().sort(function (a, b) {
      return a - b;
    });
    const goalRanks = tmpRanks.slice().map(function (v) {
      return goalSorted.indexOf(v);
    });

    for (let i = 0; i < horses.length; i++) {
      if (horses[i].field) {
        horses[i].rank = ranks[i] + goalHorses;
      } else if (this.isGoal(i)) {
        horses[i].rank = goalRanks[i];
      } else {
        // rank for dropped horses highest possible
        horses[i].rank = horses.length - 1;
      }
    }
  }

  public calcTotalRanks() {
    // calculate total rank for all horses
    const horses = this.game.horses;
    const tmpPoints: any[] = [];

    for (let i = 0; i < horses.length; i++) {
      let points = 0;
      if (horses[i].points.length > 0) {
        points = horses[i].points.reduce((total, num) => {
          return total + num;
        }, 0);
      }
      tmpPoints[i] = {
        horse: horses[i],
        points: points,
        ranks: this.getRanksOfHorse(i),
      };
    }

    const sortedPoints = tmpPoints.slice().sort((a, b) => {
      if (b.points === a.points) {
        // TODO implement logic for same points
        for (let i = 0; i < horses.length; i++) {
          if (b.ranks[i] !== a.ranks[i]) {
            return b.ranks[i] - a.ranks[i];
          }
        }
        if (Turfmaster.hurdleRounds.length > 0) {
          const ranksOfRound = this.getRanksOfRound(Turfmaster.hurdleRounds[0]);
          return ranksOfRound[tmpPoints.indexOf(b)] - ranksOfRound[tmpPoints.indexOf(a)];
        } else {
          return 0;
        }
      } else {
        return b.points - a.points;
      }
    });

    const rankPoints = tmpPoints.slice().map(function (v) {
      return sortedPoints.indexOf(v);
    });

    for (let i = 0; i < horses.length; i++) {
      horses[i].totalrank = rankPoints[i];
    }

    // ####################################
    // calculate total rank for all players

    // 0. init array
    const pointsForPlayer: {
      playerid: number;
      pointsSum: number;
      highestHorseTotalrank: number;
    }[] = [];
    for (let p = 0; p < this.game.players.length; p++) {
      // find highest horse totalrank for this player
      let highestHorseTotalrank = -1;
      this.game.horses.forEach((horse) => {
        if (horse.playerid === p && horse.totalrank > highestHorseTotalrank) {
          highestHorseTotalrank = horse.totalrank;
        }
      });

      pointsForPlayer.push({ playerid: p, pointsSum: 0, highestHorseTotalrank });
    }

    // 1. sum up points for each player
    for (let i = 0; i < horses.length; i++) {
      const horse = horses[i];

      // calculate sum of points for horse
      const pointsSum = horse.points.reduce((sum: number, current: number) => {
        return sum + current;
      }, 0);

      const playerToUpdate = pointsForPlayer.find((p) => p.playerid === horse.playerid);
      if (playerToUpdate !== undefined) {
        playerToUpdate.pointsSum += pointsSum;
      }
    }

    // 2. sort by pointsSum for each player
    // if two elements in pointsForPlayer have the same sum, check who has the horse with the highest horse.totalrank
    const sortedPointsForPlayer = pointsForPlayer.slice().sort((a, b) => {
      if (a.pointsSum === b.pointsSum) {
        return b.highestHorseTotalrank - a.highestHorseTotalrank;
      } else {
        return b.pointsSum - a.pointsSum;
      }
    });

    // 3. assign player.totalranks based on sorted array
    for (let r = 0; r < sortedPointsForPlayer.length; r++) {
      const playerData = sortedPointsForPlayer[r];
      this.game.players[playerData.playerid].totalrank = r;
    }
  }

  /**
   * CalcHandi
   * @method Turfmaster.Controller#calcHandi
   */
  public calcHandi() {
    const horses = this.game.horses;
    const tmpprogs: number[] = [];
    let goalHorses = 0;

    for (let i = 0; i < horses.length; i++) {
      const field = horses[i].field;
      if (this.isGoal(i)) {
        goalHorses++;
      } else if (field) {
        tmpprogs[i] = field.prog;
      }
    }

    const sorted = tmpprogs.slice().sort((a, b) => {
      return b - a;
    });
    const handi = tmpprogs.slice().map((v) => {
      return sorted.indexOf(v);
    });

    for (let i = 0; i < horses.length; i++) {
      if (horses[i].field) {
        horses[i].handi = handi[i] + goalHorses;
      } else {
        horses[i].handi = -1;
      }
    }
  }

  /**
   * CalcPoints
   * @method Turfmaster.Controller#calcPoints
   */
  public calcPoints(horseid: number) {
    if (isNaN(horseid)) {
      gameLogger.error('error horseid is not a number');
      return false;
    }

    const horses = this.game.horses;
    const points = Turfmaster.points;
    let rank = 0;

    if (!horses[horseid]) {
      gameLogger.error('error invalid horseid');
      return false;
    }

    for (let i = 0; i < horses.length; i++) {
      if (
        i !== horseid &&
        this.isGoal(i) &&
        (horses[i].finished || this.compareFields(horses[horseid].field, horses[i].field) > 0)
      ) {
        rank++;
      }
    }

    if (!horses[horseid].points) {
      horses[horseid].points = [];
    }

    if (rank >= 0 && points[rank]) {
      horses[horseid].points.push(points[rank]);
    } else {
      horses[horseid].points.push(0);
    }

    if (horses[horseid].ranks === undefined) {
      horses[horseid].ranks = [];
    }

    horses[horseid].ranks.push(rank);
  }

  /**
   * calcTurnorder
   * @method Turfmaster.Controller#calcTurnorder
   */
  public calcTurnorder(allHorses = false) {
    const horses = this.game.horses;
    let tmphorses: Horse[] = [];

    if (!allHorses) {
      for (let i = 0; i < horses.length; i++) {
        if (horses[i].field) {
          tmphorses.push(horses[i]);
        }
      }
    } else {
      tmphorses = horses;
    }

    const sorted = tmphorses.slice().sort((a, b) => {
      return a.rank - b.rank;
    });

    this.game.turnorder = sorted.slice().map((v) => {
      return horses.indexOf(v);
    });

    this.game.turnpos = 0;
  }

  /**
   * calcTurndices
   * @method Turfmaster.Controller#calcTurndices
   */
  public calcTurndices() {
    const dices: number[] = [];
    this.game.turndices = dices;

    for (let i = 0; i < Turfmaster.dices; i++) {
      dices[i] = randomInt(1, 6);
    }
  }

  /**
   * calcDicessum
   * @method Turfmaster.Controller#calcDicessum
   * @return {Number} Dicessum
   */
  public calcDicessum() {
    const dices = this.getDices();

    if (!dices) {
      gameLogger.error('error getDices invalid dices array');
      return false;
    }

    let dicessum = 0;

    for (let i = 0; i < dices.length; i++) {
      dicessum += dices[i];
    }

    return dicessum;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getCountOfBonusCards(horseId: number) {
    // always enforce gold card deck
    const defcards: number[] = Turfmaster.cardsGold;

    const rounds = Turfmaster.rounds;
    const cardsround = 10; // Math.floor(defcards.length / rounds);

    return defcards.length - rounds * cardsround;
  }

  /**
   * UpdateBonusCards
   * @method Turfmaster.Controller#updateBonusCards
   */
  public updateBonusCards(horseId: number): number[] {
    const horse: Horse = this.game.horses[horseId];
    const removed: number[] = [];

    if (horse.bonuscards === this.game.round - 1) {
      const cards: number[] = horse.cards[this.game.round - 1];
      const bonuscards = this.getCountOfBonusCards(horseId);

      for (let i = 0; i < bonuscards; i++) {
        if (cards.length > 0) {
          const removeId = randomInt(0, cards.length - 1);
          removed.push(cards[removeId]);
          cards.splice(removeId, 1);
        }
      }
    }

    if (horse.bonuscards === -1 && this.game.round === Turfmaster.rounds - 1) {
      this.addBonusCards(horseId);
    }

    return removed;
  }

  /**
   * AddBonusCards
   * @method Turfmaster.Controller#addBonusCards
   */
  public addBonusCards(horseid: number) {
    const cards = this.getCurrentCards(horseid);

    const horse = this.game.horses[horseid];
    let bonuscards: number[] | false;

    if (!horse) {
      gameLogger.error('error invalid horseid parameter');
      bonuscards = false;
    } else {
      bonuscards = horse.cards[Turfmaster.rounds];
    }

    if (!cards || !bonuscards || !this.game.horses[horseid] || bonuscards.length === 0) {
      gameLogger.error('error invalid values');
      return false;
    }

    for (let i = 0; i < bonuscards.length; i++) {
      horse.cards[this.game.round].push(bonuscards[i]);
    }

    horse.bonuscards = this.game.round;

    bonuscards.splice(0, bonuscards.length);

    horse.cards[this.game.round] = cards.slice().sort((a, b) => {
      return a - b;
    });

    return true;
  }

  /**
   * MoveHorse
   * @method Turfmaster.Controller#moveHorse
   * @param horseid
   * @param field
   */
  public moveHorse(horseid: number, field: Field) {
    if (!(field instanceof Field)) {
      gameLogger.error('error invalid field parameter');
      return false;
    }

    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    if (this.isBlock(horseid, field.lane, field.pos)) {
      gameLogger.error('error moveHorse: square blocked by another horse');
      return false;
    }

    const horse = this.game.horses[horseid];

    horse.field = field;
  }

  public removeHorse(horseid: number, finished: boolean, dropped: boolean) {
    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse = this.game.horses[horseid];

    horse.field = null;
    horse.dropped = dropped;
    horse.finished = finished;

    // For dropped horses remove current cards
    if (dropped) {
      const cards = this.getCurrentCards(horseid);
      if (!cards) {
        gameLogger.error('error cards invalid');
        return false;
      }
      horse.cards[this.game.round].splice(0, cards.length);
    }
  }

  /**
   * MergeCards
   * @method Turfmaster.Controller#mergeCards
   * @param horseid
   * @param {Array} added - Added
   * @param {Array} removed - Removed
   */
  public mergeCards(horseid: number, added: number[], removed: number[]) {
    if (
      !(added instanceof Array) ||
      !(removed instanceof Array) ||
      added.length !== removed.length ||
      !this.game.horses[horseid]
    ) {
      gameLogger.error('error invalid parameters');
      return false;
    }

    const horse = this.game.horses[horseid];

    const lastcards = this.getLastCards(horseid);
    let cards = this.getCurrentCards(horseid);

    if (!lastcards || !cards) {
      gameLogger.error('error lastcards or cards invalid');
      return false;
    }

    cards = [
      ...cards.filter((_card: number, index: number) => !removed.includes(index)),
      ...lastcards.filter((_card, index) => added.includes(index)),
    ];

    horse.cards[this.game.round - 1].splice(0, lastcards.length);

    horse.cards[this.game.round] = cards.slice().sort((a, b) => {
      return a - b;
    });

    return true;
  }

  /**
   * PlayCard
   * @method Turfmaster.Controller#playCard
   * @param horseid
   * @param {Number} carddiceid - Carddiceid
   */
  public playCard(horseid: number, carddiceid: number) {
    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse: Horse = this.game.horses[horseid];

    const cards = horse.cards[this.game.round];

    if (!cards || !cards[carddiceid]) {
      gameLogger.error('error horse cards invalid');
      return false;
    }

    horse.cards[this.game.round].splice(carddiceid, 1);
  }

  /**
   * GetBonusCards
   * @method Turfmaster.Controller#getBonusCards
   */
  public getBonusCards(horseid: number) {
    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse = this.game.horses[horseid];

    return horse.cards[Turfmaster.rounds];
  }

  /**
   * getDices
   * @method Turfmaster.Controller#getDices
   */
  public getDices() {
    const dices = this.game.turndices;
    const id = this.game.turndicescur;

    if (dices[id]) {
      return [dices[id]];
    } else {
      return dices;
    }
  }

  public getGameOverview(): GameOverview | null {
    // TODO add filtering of game - why not use getGameForPlayerId without playerid
    if (this.game) {
      const status: GameStatus = this.game.round > 0 ? GameStatus.Running : GameStatus.Waiting;
      return {
        gameId: this.gameid,
        timeCreated: this.timecreated,
        name: this.game.name,
        gameType: this.gameType,
        horseamount: this.game.horseamount,
        players: this.game.players,
        horses: this.game.horses,
        visibility: this.visibility,
        allowSpectators: this.allowSpectators,
        chatVisibility: this.chatVisibility,
        status,
      };
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getGameForPlayerId(playerid: number) {
    // TODO add filtering for certain playerid and add default filter without playerid
    return this.game;
  }

  public getGameSnapshot(): Game | null {
    return this.game;
  }

  /**
   * returns the horseId for the horse whose turn it is
   * @method Turfmaster.Controller#getHorseId
   */
  public getHorseId(): number {
    return this.game.turnorder[this.game.turnpos];
  }

  /**
   * GetMoves
   * @method Turfmaster.Controller#getMoves
   * @param horseid
   * @param {Number} count - Count
   * @return {Array} Moves
   */
  public getMoves(horseid: number, count: number) {
    if (isNaN(count)) {
      gameLogger.error('error invalid count parameter');
      return false;
    }

    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse = this.game.horses[horseid];
    const fields = this.board.fields;

    if (!horse || !horse.field) {
      gameLogger.error('error invalid horse or horse field');
      return false;
    }

    // Array to store all possible move paths
    const moves: Move[] = [];
    // Array to track how many steps each move has used
    const movecount: number[] = [];
    // Array to track if a horse has changed lanes in a move
    // Only at most two lane changes are allowed
    // The first step can be a lane change
    // At least the next five have to be straight
    // After that, a horse can change lanes again only once after the 6th step
    // If this is true, the horse has changed lanes in this move or it is too late to change lanes
    const turned: boolean[] = [];

    let tmpmoves;
    let tmpmovecount;
    let hurdle;
    let block;
    let field;
    let jump = 5; // After 5 steps, a horse can change lanes only once

    count = Math.abs(count);

    moves[0] = new Move();
    moves[0].fields.push(horse.field); // Start with current horse position
    movecount[0] = 0; // No steps taken yet

    for (let i = 0; i < count; i++) {
      tmpmoves = [];
      tmpmovecount = [];
      for (let j = 0; j < moves.length; j++) {
        // Only continue moves that haven't reached the maximum step count
        if (movecount[j] < count) {
          field = moves[j].fields[moves[j].fields.length - 1];
          // First step or after 5 steps, a horse can change lanes
          if (movecount[j] === 0 || (movecount[j] > jump && turned[j] !== true)) {
            // Check if left lane change is possible
            hurdle = this.isHurdle(field.lane - 1, field.nl);
            block = this.isBlock(horseid, field.lane - 1, field.nl);
            if (field.nl !== -1 && !block && !hurdle) {
              tmpmoves.push(new Move());
              for (let k = 0; k < moves[j].fields.length; k++) {
                tmpmoves[tmpmoves.length - 1].fields[k] = moves[j].fields[k];
              }
              tmpmoves[tmpmoves.length - 1].fields[moves[j].fields.length] =
                fields[field.lane - 1][field.nl];
              tmpmovecount[tmpmoves.length - 1] = movecount[j] + 1;
            }

            // Check if right lane change is possible
            hurdle = this.isHurdle(field.lane + 1, field.nr);
            block = this.isBlock(horseid, field.lane + 1, field.nr);
            if (field.nr !== -1 && !block && !hurdle) {
              tmpmoves.push(new Move());
              for (let k = 0; k < moves[j].fields.length; k++) {
                tmpmoves[tmpmoves.length - 1].fields[k] = moves[j].fields[k];
              }
              tmpmoves[tmpmoves.length - 1].fields[moves[j].fields.length] =
                fields[field.lane + 1][field.nr];
              tmpmovecount[tmpmoves.length - 1] = movecount[j] + 1;
            }
          }

          // Check if the horse can move forward
          if (field.nf !== -1) {
            hurdle = this.isHurdle(field.lane, field.nf);
            block = this.isBlock(horseid, field.lane, field.nf);
            if (!block) {
              if (!hurdle) {
                moves[j].fields[moves[j].fields.length] = fields[field.lane][field.nf];
                movecount[j] += 1;
              } else {
                block = this.isBlock(horseid, field.lane, fields[field.lane][field.nf].nf);
                if (count > movecount[j] + 3 && fields[field.lane][field.nf].nf !== -1 && !block) {
                  moves[j].fields[moves[j].fields.length] = fields[field.lane][field.nf];
                  moves[j].fields[moves[j].fields.length] =
                    fields[field.lane][fields[field.lane][field.nf].nf];
                  movecount[j] += 4;
                  // Check if horse is direct in front of hurdle to set jump after 7 and not 6 fields
                  if (
                    moves.length === 1 &&
                    this.isHurdle(moves[0].fields[0].lane, moves[0].fields[0].nf)
                  ) {
                    jump = 6;
                  }
                }
              }
            }
          } else {
            moves[j].drop = true;
          }
        }
      }
      for (let j = 0; j < tmpmoves.length; j++) {
        moves.push(tmpmoves[j]);
        movecount[moves.length - 1] = tmpmovecount[j];
        if (movecount[moves.length - 1] > jump) {
          turned[moves.length - 1] = true;
        }
      }
    }

    let maxcount;
    let max = 0;

    for (let i = 0; i < moves.length; i++) {
      maxcount = 0;
      for (let j = 0; j < moves[i].fields.length; j++) {
        hurdle = this.isHurdle(moves[i].fields[j].lane, moves[i].fields[j].pos);
        if (hurdle) {
          maxcount += 3;
        } else {
          maxcount += 1;
        }
      }
      if (max < maxcount) {
        max = maxcount;
      }
    }

    let returnmoves: Move[] = [];
    let returncount = 0;
    let found: boolean;

    for (let i = 0; i < moves.length; i++) {
      maxcount = 0;
      for (let j = 0; j < moves[i].fields.length; j++) {
        hurdle = this.isHurdle(moves[i].fields[j].lane, moves[i].fields[j].pos);
        if (hurdle) {
          maxcount += 3;
        } else {
          maxcount += 1;
        }
      }
      if (maxcount === max) {
        found = false;
        for (let k = 0; k < returnmoves.length; k++) {
          if (
            returnmoves[k].fields[returnmoves[k].fields.length - 1] ===
            moves[i].fields[moves[i].fields.length - 1]
          ) {
            found = true;
            break;
          }
        }
        if (!found) {
          returnmoves[returncount] = moves[i];
          returncount++;
        }
      }
    }

    const _this = this;
    returnmoves = returnmoves.slice().sort((a, b) => {
      return _this.compareFields(a.fields[a.fields.length - 1], b.fields[b.fields.length - 1]);
    });

    return returnmoves;
  }

  /**
   * GetMovesLogic
   * @method Turfmaster.Controller#getMovesLogic
   * @param {Number} horseid - Horseid
   * @param {Number} carddiceid - Carddiceid (index into cards/dices array, or -1 for "any card/dice")
   * @param {Boolean} skipHandi - SkipHandi (if true, don't apply handicap conversion for card turns)
   * @return {Array|false} Moves array, empty array if carddiceid === -1, or false on error
   *
   * Calculates all valid moves for a horse given a specific card or dice.
   *
   * Special cases:
   * - carddiceid === -1: Returns empty array [] (used by setNextMove to check if ANY card/dice has moves)
   * - Invalid carddice: Returns false (error case)
   *
   * Card turns:
   * - Applies handicap logic: if horse has handicap and skipHandi !== true, converts card value to 0
   * - Gets moves using card value (number of steps)
   * - Marks moves as drops if they end on a hurdle without using the full card value
   *   (moves[i].fields.length !== Math.abs(carddice) + 1 && hurdle)
   *
   * Dice turns:
   * - Gets moves using dice value directly (no handicap conversion or drop logic)
   * - Returns moves as-is
   *
   * Used by:
   * - setNextMove: Checks if horse has any valid moves (carddiceid: -1)
   * - selectCardDiceEvent: Validates selected card/dice has moves
   * - selectMoveEvent: Validates moveid is within valid moves
   * - Bot (tmcomputer.ts): Evaluates which card/dice to select
   */
  public getMovesLogic(
    horseid: number,
    carddiceid: number,
    skipHandi: boolean,
  ): readonly Move[] | false {
    let carddice = this.getCardDice(horseid, carddiceid);

    // Invalid card/dice (e.g., invalid index, no cards available)
    if (!carddice && carddice !== 0) {
      return false;
    }

    // carddiceid === -1 means "any card/dice" - return empty array to indicate no specific card/dice selected
    // Used by setNextMove to check if horse has ANY valid moves without selecting a specific card/dice
    if (carddice === -1) {
      return [];
    }

    if (this.game.turntype === Turfmaster.turntypes.CARDS) {
      // Card turns: apply handicap logic (convert to 0 if handicap applies)
      if (skipHandi !== true && this.isHandi(horseid, carddice)) {
        carddice = 0;
      }

      const moves = this.getMoves(horseid, carddice);

      if (!moves) {
        gameLogger.error('error getMoves invalid result');
        return false;
      }

      // Card turns: mark moves as drops if they end on a hurdle without using full card value
      // A move must use exactly Math.abs(carddice) + 1 fields (including start position)
      // If it ends on a hurdle with fewer fields, it's a drop (horse falls)
      let field: Field;
      let hurdle: boolean;

      for (let i = 0; i < moves.length; i++) {
        field = moves[i].fields[moves[i].fields.length - 1];
        hurdle = this.isHurdle(field.lane, field.nf);
        if (moves[i].fields.length !== Math.abs(carddice) + 1 && hurdle) {
          moves[i].fields[moves[i].fields.length] = this.board.fields[field.lane][field.nf];
          moves[i].drop = true;
        }
      }
      return moves;
    } else {
      // Dice turns: get moves directly (no handicap conversion, no drop logic for hurdles)
      const moves = this.getMoves(horseid, carddice);

      if (!moves) {
        gameLogger.error('error getMoves invalid result');
        return false;
      }
      return moves;
    }
  }

  /**
   * Check if horse has any valid moves for any card/dice.
   * Used by setNextMove to determine if player should be skipped when no moves are available.
   * @method Turfmaster.Controller#hasAnyValidMoves
   * @param {Number} horseid - Horseid
   * @return {Boolean} True if horse has at least one valid move for any card/dice, false otherwise
   */
  private hasAnyValidMoves(horseid: number): boolean {
    if (this.game.turntype === Turfmaster.turntypes.CARDS) {
      const cards = this.getCurrentCards(horseid);
      if (cards && cards.length > 0) {
        for (let i = 0; i < cards.length; i++) {
          const cardMoves = this.getMovesLogic(horseid, i, false);
          if (cardMoves && cardMoves.length > 0) {
            return true;
          }
        }
      }
    } else {
      const dices = this.getDices();
      if (dices && dices.length > 0) {
        for (let i = 0; i < dices.length; i++) {
          const diceMoves = this.getMovesLogic(horseid, i, false);
          if (diceMoves && diceMoves.length > 0) {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * GetCurrentCards
   * @method Turfmaster.Controller#getCurrentCards
   */
  public getCurrentCards(horseid: number): false | readonly number[] {
    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse: Horse = this.game.horses[horseid];

    return horse.cards[this.game.round];
  }

  /**
   * GetLastCards
   * @method Turfmaster.Controller#getLastCards
   */
  public getLastCards(horseid: number): null | false | readonly number[] {
    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse = this.game.horses[horseid];

    if (this.game.round - 1 >= 0) {
      return horse.cards[this.game.round - 1];
    }

    return null;
  }

  /**
   * returns the playerId for the player whose turn it is
   * @method Turfmaster.Controller#getPlayerId
   */
  public getPlayerId() {
    const horseid = this.getHorseId();

    if (!this.game.horses[horseid]) {
      gameLogger.error('error getHorseId invalid horseid result');
      return false;
    }

    const horse: Horse = this.game.horses[horseid];

    return horse.playerid;
  }

  public isBlock(horseid: number, lane: number, pos: number) {
    const horses: Horse[] = this.game.horses;

    for (let i = 0; i < horses.length; i++) {
      if (
        i !== horseid &&
        horses[i] &&
        horses[i].field &&
        horses[i].field!.lane === lane &&
        horses[i].field!.pos - 1 <= pos &&
        horses[i].field!.pos + 1 >= pos
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * IsGoal
   * @method Turfmaster.Controller#isGoal
   * @return {Boolean} True/False
   */
  public isGoal(horseid: number): boolean {
    const horses = this.game.horses;

    if (!horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const field = horses[horseid].field;
    if (horses[horseid].dropped || (field && field.prog < this.board.goal)) {
      return false;
    }

    return true;
  }

  /**
   * IsHandi
   * @method Turfmaster.Controller#isHandi
   * @param horseid
   * @param {Number} count - Count
   * @return {Boolean} True/False
   */
  public isHandi(horseid: number, count: number) {
    if (isNaN(count)) {
      gameLogger.error('error invalid count parameter');
      return false;
    }

    if (!this.game.horses[horseid]) {
      gameLogger.error('error invalid horseid parameter');
      return false;
    }

    const horse: Horse = this.game.horses[horseid];
    const handi = Turfmaster.handi;

    for (let i = 0; i < handi.length; i++) {
      if (horse.handi === i && count > handi[i]) {
        return true;
      }
    }

    return false;
  }

  public isHorseLeft() {
    return this.game.horses.length < this.game.horseamount;
  }

  public isHurdle(lane: number, pos: number) {
    return (
      Turfmaster.hurdleRounds.indexOf(this.game.round) !== -1 &&
      this.board.fields[lane] &&
      this.board.fields[lane][pos] &&
      this.board.fields[lane][pos].hurdle
    );
  }

  /**
   * CompareFields
   * @method Turfmaster.Controller#compareFields
   * @param {Object} a - A
   * @param {Object} b - B
   */
  public compareFields(a: Field | null, b: Field | null) {
    if (!(a instanceof Field) || !(b instanceof Field)) {
      return 0;
    }

    const tolerance = 0.01;

    if (Math.abs(a.prog - b.prog) < tolerance) {
      return a.lane - b.lane;
    } else if (a.prog < b.prog) {
      return 1;
    } else {
      return -1;
    }
  }

  public shuffle(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = randomInt(0, i);
      const temp = array[i];
      array[i] = array[j];
      array[j] = temp;
    }
  }
}

export default ControllerLocal;
